import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Text,
  Input,
  Select,
  Checkbox,
  useToast,
  HStack,
  SimpleGrid,
  Box,
} from '@chakra-ui/react';
import AdminDrawerContent from '../layout/AdminDrawerContent';
import { AdminField, useAdminFormFieldProps } from '../layout/AdminFormControls';
import ModelMultiSearchSelect from '../Services/ModelMultiSearchSelect';
import { getData } from '../../utils/httpClient';

const EMPTY_FORM = {
  id: null,
  code: '',
  name: '',
  categoryId: '',
  currentCost: '',
  currentStock: '',
  minStock: 5,
  notifyLowStock: true,
  unitId: '',
  providerId: '',
  currencyId: '',
  ivaIncluded: false,
};

const toOptionalId = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const ProductsEditModal = ({ isOpen, onClose, product, isNew, onCreate, onUpdate }) => {
  const [formData, setFormData] = React.useState(EMPTY_FORM);
  const [providers, setProviders] = React.useState([]);
  const [currencyList, setCurrencyList] = React.useState([]);
  const [unitsList, setUnitsList] = React.useState([]);
  const [categoriesList, setCategoriesList] = React.useState([]);
  const [modelsList, setModelsList] = React.useState([]);
  const [selectedModelIds, setSelectedModelIds] = React.useState([]);
  const toast = useToast();
  const { fieldProps } = useAdminFormFieldProps();

  const getCurrency = async () => {
    try {
      const response = await getData('/currencies');
      if (response?.payload?.currencies) {
        setCurrencyList(response.payload.currencies);
      }
    } catch (error) {
      setCurrencyList([]);
      toast({
        title: 'Error al obtener tipos de moneda',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getUnits = async () => {
    try {
      const response = await getData('/units');
      if (response?.payload?.units) {
        setUnitsList(response.payload.units);
      }
    } catch (error) {
      setUnitsList([]);
      toast({
        title: 'Error al obtener unidades de productos',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getModels = async () => {
    try {
      const response = await getData('/models');
      if (response?.payload?.models) {
        setModelsList(
          [...response.payload.models].sort((a, b) =>
            String(a?.description || '').localeCompare(
              String(b?.description || ''),
              'es',
              { sensitivity: 'base' },
            ),
          ),
        );
      }
    } catch {
      setModelsList([]);
    }
  };

  const getCategories = async () => {
    try {
      const response = await getData('/categories');
      if (response?.payload?.categories) {
        setCategoriesList(response.payload.categories);
      }
    } catch (error) {
      setCategoriesList([]);
      toast({
        title: 'Error al obtener categorias',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getProviders = async () => {
    try {
      const response = await getData('/providers');
      if (response?.payload?.providers && Array.isArray(response.payload.providers)) {
        setProviders(response.payload.providers);
      } else {
        throw new Error('Formato inesperado en la respuesta de proveedores.');
      }
    } catch (error) {
      setProviders([]);
      toast({
        title: 'Error al obtener proveedores',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    getProviders();
    getCurrency();
    getUnits();
    getCategories();
    getModels();

    if (product) {
      const providerId = product?.providerId ?? product?.provider?.id ?? null;
      setFormData({
        id: product?.id ?? null,
        code: product?.code ?? '',
        name: product?.name ?? '',
        categoryId: product?.categoryId ?? product?.category?.id ?? '',
        currentCost: product?.currentCost ?? '',
        currentStock: product?.currentStock ?? '',
        minStock: product?.minStock ?? 5,
        notifyLowStock:
          product?.notifyLowStock === undefined
            ? true
            : Boolean(product.notifyLowStock),
        unitId: product?.unitId ?? product?.unit?.id ?? '',
        providerId: providerId != null ? String(providerId) : '',
        currencyId: product?.currencyId ?? product?.currency?.id ?? '',
        ivaIncluded: Boolean(product?.ivaIncluded),
      });
      const fromLinks = (product?.productModels || [])
        .map((pm) => pm.modelId ?? pm.model?.id)
        .filter(Boolean)
        .map(Number);
      if (fromLinks.length) {
        setSelectedModelIds(fromLinks);
      } else if (product?.modelId || product?.model?.id) {
        setSelectedModelIds([Number(product.modelId ?? product.model.id)]);
      } else {
        setSelectedModelIds([]);
      }
    } else {
      setFormData({ ...EMPTY_FORM, currencyId: '' });
      setSelectedModelIds([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, isNew, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    const name = String(formData.name || '').trim();
    const currentCost = parseFloat(String(formData.currentCost ?? '').replace(',', '.'));
    if (!name) {
      toast({
        title: 'Ingresá el nombre del producto',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (name.length > 100) {
      toast({
        title: 'El nombre no puede superar 100 caracteres',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!Number.isFinite(currentCost)) {
      toast({
        title: 'Ingresá un precio válido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const { currentStock: _s, id: _id, ...formWithoutStock } = formData;
    const updatedData = {
      ...formWithoutStock,
      name,
      categoryId: toOptionalId(formData.categoryId),
      unitId: toOptionalId(formData.unitId),
      providerId: toOptionalId(formData.providerId),
      currencyId: toOptionalId(formData.currencyId),
      currentCost,
      minStock: Math.max(0, parseInt(formData.minStock, 10) || 0),
      notifyLowStock: Boolean(formData.notifyLowStock),
      ivaIncluded: Boolean(formData.ivaIncluded),
      modelIds: selectedModelIds.map(Number),
      modelId: selectedModelIds[0] ? Number(selectedModelIds[0]) : null,
    };

    try {
      if (isNew) {
        await onCreate({ ...updatedData, currentStock: 0 });
        toast({
          title: 'Producto creado',
          description: 'El nuevo producto se ha guardado exitosamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await onUpdate({ ...updatedData, id: formData.id });
        toast({
          title: 'Producto actualizado',
          description: 'Los cambios se han guardado exitosamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
    } catch (error) {
      toast({
        title: isNew ? 'Error al crear el producto' : 'Error al guardar producto',
        description: error.message || 'No se pudo completar la operación.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={false}>
      <DrawerOverlay />
      <AdminDrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="md" fontWeight="bold" textAlign="center" py={3}>
          {isNew ? 'Nuevo producto' : 'Editar producto'}
        </DrawerHeader>
        <DrawerBody py={3} px={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            {!isNew && (
              <AdminField label="ID">
                <Input
                  {...fieldProps}
                  value={formData.id ?? ''}
                  isReadOnly
                  bg="gray.100"
                />
              </AdminField>
            )}
            <AdminField label="Código">
              <Input
                {...fieldProps}
                name="code"
                value={formData.code ?? ''}
                onChange={handleChange}
                placeholder="Código"
              />
            </AdminField>
            <AdminField label="Nombre">
              <Input
                {...fieldProps}
                name="name"
                value={formData.name ?? ''}
                onChange={handleChange}
                placeholder="Nombre del producto"
              />
            </AdminField>
            <AdminField label="Categoría">
              <Select
                {...fieldProps}
                name="categoryId"
                value={formData.categoryId ?? ''}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Seleccionar categoría
                </option>
                {categoriesList?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </AdminField>
            <Box gridColumn={{ base: '1', md: '1 / -1' }}>
              <ModelMultiSearchSelect
                models={modelsList}
                selectedIds={selectedModelIds}
                onChange={setSelectedModelIds}
                label="Modelos de vehículo"
                placeholder="Buscar modelo para agregar…"
                hint="Sin modelos seleccionados = aplica a todos los vehículos."
              />
            </Box>
            <AdminField label="Precio actual">
              <Input
                {...fieldProps}
                name="currentCost"
                type="number"
                value={formData.currentCost ?? ''}
                onChange={handleChange}
                placeholder="Precio"
                step="0.01"
                min="0"
              />
            </AdminField>
            <AdminField label="IVA">
              <Checkbox
                mt={1}
                isChecked={Boolean(formData.ivaIncluded)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ivaIncluded: e.target.checked,
                  }))
                }
              >
                Precio con IVA incluido
              </Checkbox>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Si está marcado, la factura de compra toma el costo como IVA inc.
              </Text>
            </AdminField>
            <AdminField label="Stock">
              <Input
                {...fieldProps}
                name="currentStock"
                type="number"
                value={product?.currentStock ?? formData.currentStock ?? 0}
                isReadOnly
                bg="gray.100"
              />
            </AdminField>
            <AdminField label="Alerta stock bajo">
              <Input
                {...fieldProps}
                name="minStock"
                type="number"
                min="0"
                step="1"
                value={formData.minStock ?? 5}
                onChange={handleChange}
                placeholder="Ej: 3"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Avisa cuando el stock sea menor o igual a este valor (ej. 3).
              </Text>
            </AdminField>
            <AdminField label="Notificaciones">
              <Checkbox
                mt={1}
                isChecked={Boolean(formData.notifyLowStock)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    notifyLowStock: e.target.checked,
                  }))
                }
              >
                Notificarme stock bajo de este producto
              </Checkbox>
            </AdminField>
            <AdminField label="Unidad">
              <Select
                {...fieldProps}
                name="unitId"
                value={formData.unitId ?? ''}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Seleccionar unidad
                </option>
                {unitsList?.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            </AdminField>
            <AdminField label="Moneda">
              <Select
                {...fieldProps}
                name="currencyId"
                value={formData.currencyId ?? ''}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Seleccionar moneda
                </option>
                {currencyList?.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.code}
                  </option>
                ))}
              </Select>
            </AdminField>
            <AdminField label="Proveedor">
              <Select
                {...fieldProps}
                name="providerId"
                value={formData.providerId ?? ''}
                onChange={handleChange}
              >
                <option value="">Sin proveedor</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </Select>
            </AdminField>
          </SimpleGrid>
        </DrawerBody>
        <HStack justify="flex-end" p={4} pt={2}>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="adminPrimary" size="sm" onClick={handleSave}>
            Guardar
          </Button>
        </HStack>
      </AdminDrawerContent>
    </Drawer>
  );
};

export default ProductsEditModal;
